import { Image } from 'expo-image';
import React, { useMemo } from 'react';
import { Modal, StyleSheet, TouchableWithoutFeedback, View } from 'react-native';

import { Button, IconClose, Text, createMqStyles, tokens, useMqSelect } from '@fhs/ui';
import { create } from '@fhs/zustand';
import { ILoyaltyQuestUi } from '@fhs-legacy/frontend/src/generated/graphql-gateway';

import { useOnPressChallenge } from '../../hooks/use-on-press-challenge';
import { useChallengesUi } from '../../queries/loyalty.queries';
import { getChallengeButtonText } from '../../utils';

interface IChallengeDetailsModalStore {
  challengeId?: string;
  show: (challengeId: string) => void;
  close: () => void;
}

export const useChallengeDetailsModalStore = create<IChallengeDetailsModalStore>(set => ({
  challengeId: undefined,
  show: (challengeId: string) =>
    set({
      challengeId,
    }),
  close: () => set({ challengeId: undefined }),
}));

export const ChallengeDetailsModal = () => {
  const mqStyle = useMqStyles();
  const closeButtonSize = useMqSelect({ $gteDesktop: 32 }, 24);
  const { data } = useChallengesUi();
  const { challengeId, close } = useChallengeDetailsModalStore();
  const onPressChallenge = useOnPressChallenge();

  const selectedChallenge = useMemo(
    () => data && data.find(challenge => challenge.id === challengeId),
    [data, challengeId]
  );

  const buttonLabel = useMemo(
    () =>
      selectedChallenge?.status ? getChallengeButtonText(selectedChallenge.status) : undefined,
    [selectedChallenge]
  );

  const handleOnPressChallenge = (challenge: ILoyaltyQuestUi) => {
    close();
    onPressChallenge(challenge, 'Details Modal');
  };

  return (
    <>
      {selectedChallenge && (
        <Modal visible={!!challengeId} transparent onRequestClose={close}>
          <View style={mqStyle.modal}>
            <TouchableWithoutFeedback onPress={close}>
              <View style={mqStyle.backdrop} />
            </TouchableWithoutFeedback>
            <View style={mqStyle.container}>
              <Image source={{ uri: selectedChallenge.image }} style={mqStyle.image} />
              <View style={mqStyle.content}>
                <Text.Heading type="one">{selectedChallenge.name}</Text.Heading>
                <Text.Ui size="md" style={{ marginTop: 12 }}>
                  {selectedChallenge.description}
                </Text.Ui>
                {buttonLabel && (
                  <Button
                    size="xl"
                    style={mqStyle.button}
                    onPress={() => handleOnPressChallenge(selectedChallenge)}
                  >
                    <Button.Text>{buttonLabel}</Button.Text>
                  </Button>
                )}
              </View>
              <Button size="xl" type="unstyled" style={mqStyle.closeButton} onPress={close}>
                <Button.Icon size={closeButtonSize}>
                  <IconClose />
                </Button.Icon>
              </Button>
            </View>
          </View>
        </Modal>
      )}
    </>
  );
};

const useMqStyles = createMqStyles({
  modal: {
    $base: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
  backdrop: {
    $base: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: tokens.colors.$blackOpacity75,
    },
  },
  container: {
    $base: {
      marginHorizontal: 20,
      borderRadius: 12,
      backgroundColor: tokens.colors.$white,
      maxHeight: '90%',
      overflow: 'hidden',
    },
    $gteDesktop: {
      maxWidth: 526,
    },
  },
  image: {
    $base: {
      borderBottomWidth: 1,
      borderColor: tokens.colors.$blackOpacity04,
      aspectRatio: 2,
      width: '100%',
    },
  },
  content: {
    $base: {
      padding: 24,
    },
    $gteDesktop: {
      padding: 28,
      paddingTop: 24,
    },
  },
  button: {
    $base: {
      marginTop: 20,
    },
    $gteDesktop: {
      marginTop: 28,
    },
  },
  closeButton: {
    $base: {
      position: 'absolute',
      right: 0,
      top: 0,
      margin: 2,
    },
  },
});
